import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type PartnerTier = 'MAIN' | 'GOLD' | 'SILVER' | 'MEDIA' | 'PREMIUM' | 'TECHNICAL' | 'OFFICIAL';

export interface PartnerSocialLink {
  platform: string;
  url: string;
}

export interface PartnerDto {
  id: string;
  name: string;
  description: { it?: string; en?: string } | null;
  logoUrl: string;
  websiteUrl: string | null;
  socialLinks: PartnerSocialLink[];
  tier: PartnerTier;
  position: number;
  isActive: boolean;
}

export interface CreatePartnerInput {
  name: string;
  description?: { it?: string; en?: string };
  logoUrl: string;
  websiteUrl?: string;
  socialLinks?: PartnerSocialLink[];
  tier?: PartnerTier;
  position?: number;
  isActive?: boolean;
}

export type UpdatePartnerInput = Partial<CreatePartnerInput>;

interface PartnersFilters {
  tier?: PartnerTier;
  active?: boolean;
}

const QK = {
  list: (filters: PartnersFilters) => ['partners', filters] as const,
};

export function usePartners(filters: PartnersFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | boolean> = {};
      if (filters.tier) params.tier = filters.tier;
      if (filters.active !== undefined) params.active = filters.active;
      const res = await apiClient.get<{ data: PartnerDto[] }>('/partners', { params });
      return res.data.data;
    },
  });
}

export function useCreatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePartnerInput) => {
      const res = await apiClient.post<PartnerDto>('/partners', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners'] }),
  });
}

export function useUpdatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePartnerInput }) => {
      const res = await apiClient.patch<PartnerDto>(`/partners/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners'] }),
  });
}

export function useDeletePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/partners/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partners'] }),
  });
}
